/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Backdrop,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import ApiManager from "../../../../Services/ApiManager/ApiManager";
import AddVehicleDialog from "./AddVehicleDialog";
import DeleteVehicleConfirmationDialog from "./DeleteVehicleConfirmationDialog";

// Custom Delete Icon SVG Component
const CustomDeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.55534 16C3.06645 16 2.64793 15.8259 2.29978 15.4778C1.95163 15.1296 1.77756 14.7111 1.77756 14.2222V2.66667H0.888672V0.888889H5.33312V0H10.6664V0.888889H15.1109V2.66667H14.222V14.2222C14.222 14.7111 14.0479 15.1296 13.6998 15.4778C13.3516 15.8259 12.9331 16 12.4442 16H3.55534ZM12.4442 2.66667H3.55534V14.2222H12.4442V2.66667ZM5.33312 12.4444H7.11089V4.44444H5.33312V12.4444ZM8.88867 12.4444H10.6664V4.44444H8.88867V12.4444Z" fill="#121415" fillOpacity="0.6"/>
  </svg>
);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${theme.breakpoints.down('sm')}`]: {
    padding: "4px 2px",
  },
  padding: "8px 16px",
  borderBottom: "1px solid #c6c6c6",
  borderLeft: "none",
  borderRight: "none",
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#f3f3f3",
  },
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));



export default function VehicleDetailsDialog({ 
  open, 
  onClose, 
  vehicleListName,
  portfolioId,
  vehicleListId,
  associatedPackages,
  startDateUTCString,
  endDateUTCString,
  forceUninstallRequired,
  onRefresh
}) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  
  // Date and checkbox states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [forceUninstall, setForceUninstall] = useState(false);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [startDateChanged, setStartDateChanged] = useState(false);
    
  // Convert UTC timestamp to a Date object that displays as if it were local time
  // This is needed because DatePicker displays dates in local timezone
  const utcTimestampToDisplayDate = (utcTimestamp) => {
    if (!utcTimestamp) return null;
    const date = new Date(utcTimestamp);
    // Create a new date using UTC values as local values for display
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
  };

  const toUtcStartDateTime = (dateValue) => {
    return Date.UTC(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      0,
      0,
      0
    );
  };

  const toUtcEndDateTime = (dateValue) => {
    return Date.UTC(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      23,
      59,
      0
    );
  };
  // Fetch vehicles when dialog opens and vehicleListId is available
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        
        const response = await ApiManager.getVehicleListForGroup(portfolioId, vehicleListId);
        
        // Handle different response formats
        if (Array.isArray(response?.data)) {
          setVehicles(response.data);
        } else if (response && Array.isArray(response)) {
          setVehicles(response);
        } else {
          setVehicles([]);
        }
      } catch {
        setSnackbarMessage("Failed to load vehicles. Please try again.");
        setOpenSnackbar(true);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    if (open && portfolioId && vehicleListId) {
      fetchVehicles();
    }
  }, [open, portfolioId, vehicleListId]);

  useEffect(() => {
    setStartDate(startDateUTCString ? utcTimestampToDisplayDate(startDateUTCString) : null);
    setEndDate(endDateUTCString ? utcTimestampToDisplayDate(endDateUTCString) : null);
    setForceUninstall(forceUninstallRequired || false);
    setStartDateChanged(false);
  }, [startDateUTCString, endDateUTCString, forceUninstallRequired]);

  const modifyTheDates = (newStartDate, newEndDate) => {
    // Convert dates to UTC format if they exist
    const utcStartDate = newStartDate ? toUtcStartDateTime(newStartDate) : null;
    const utcEndDate = newEndDate ? toUtcEndDateTime(newEndDate) : "";
    return { utcStartDate, utcEndDate };
  };

  const handleSaveDates = async () => {
    if (startDateError || endDateError) {
      return;
    }

    try {
      setLoading(true);
      const { utcStartDate, utcEndDate } = modifyTheDates(startDate, endDate);

      // Build payload - only include startDate if it was changed
      const payload = {
        endDate: utcEndDate,
        forceUninstallRequired: forceUninstall,
      };
      
      if (startDateChanged) {
        payload.startDate = utcStartDate;
      }

      await ApiManager.patchGroupForApplication(portfolioId, vehicleListId, payload);

      setSnackbarMessage("Dates updated successfully!");
      setOpenSnackbar(true);

      if (onRefresh) {
        await onRefresh();
      }
    } catch {
      setSnackbarMessage("Failed to update dates. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = (vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Call the API to delete the vehicle from the group
      const memberId = vehicleToDelete.id || vehicleToDelete.vehicleId;
      await ApiManager.deleteVehicleFromGroup(portfolioId, vehicleListId, memberId);
      
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
      setSnackbarMessage("Vehicle deleted successfully!");
      setOpenSnackbar(true);
      
      // Refresh the vehicle list
      if (portfolioId && vehicleListId) {
        try {
          const response = await ApiManager.getVehicleListForGroup(portfolioId, vehicleListId);
          
          if (Array.isArray(response?.data)) {
            setVehicles(response.data);
          } else if (Array.isArray(response)) {
            setVehicles(response);
          } else {
            setVehicles([]);
          }
        } catch {
          // refresh error handled
        }
      }
    } catch {
      // delete error handled
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
      setSnackbarMessage("Failed to delete vehicle. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
  };

  const handleAddVehicle = () => {
    setAddVehicleDialogOpen(true);
  };

  const handleAddVehicleSubmit = async (vehicleData) => {
    // The API call is now handled in AddVehicleDialog
    // Just show success toast and refresh the vehicle list
    setSnackbarMessage("Vehicle added successfully!");
    setOpenSnackbar(true);
    
    // Refresh the vehicle list
    if (portfolioId && vehicleListId) {
      try {
        const response = await ApiManager.getVehicleListForGroup(portfolioId, vehicleListId);
        
        if (Array.isArray(response?.data)) {
          setVehicles(response.data);
        } else if (Array.isArray(response)) {
          setVehicles(response);
        } else {
          setVehicles([]);
        }
      } catch {
        // refresh error handled
      }
    }
  };

  const handleCloseAddVehicleDialog = () => {
    setAddVehicleDialogOpen(false);
  };

  const handleAddVehicleError = (errorMessage) => {
    setSnackbarMessage(errorMessage);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleDialogClose = async () => {
    // Call the parent's onClose to close the dialog first
    if (onClose) {
      onClose();
      setStartDate(null);
      setEndDate(null);
      setForceUninstall(false);
      setStartDateError("");
      setEndDateError("");
      setStartDateChanged(false);
    }
    
    // Then call the parent's onRefresh to refresh the table data
    if (onRefresh) {
      try {
        await onRefresh();
      } catch {
        // refresh error handled
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              maxHeight: "90vh",
            },
          },
        }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 500,
            color: "#333",
          }}
        >
          {vehicleListName || "Vehicle List Name"}{vehicles.length > 0 ? ` (${vehicles.length})` : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
            onClick={handleAddVehicle}
            disabled={sessionStorage.getItem("readonly_OemAdmin") === "true"}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              fontWeight: 500,
              fontSize: "12px",
              textTransform: "none",
              borderRadius: 1,
              padding: "4px 8px",
              minHeight: "28px",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Add Vehicle
          </Button>
          <IconButton
            onClick={handleDialogClose}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px 24px 24px 24px" }}>
        {loading && (
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              position: "absolute",
            }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        {!loading && (
          <div>
            <Box 
              className="time-zone-container" 
              sx={{ 
                display: "flex", 
                flexDirection: "column",
                mb: 2,
                border: "1px solid  #12141533",
                padding:"10px",
                width:"96.5%"
              }}
            >
              {/* Single row with Start Date, End Date, Checkbox, and Modify Button */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 2, 
                flexWrap: "nowrap",
                fontSize: "14px", 
                fontWeight: 600, 
                color: "#2A2C2D",
                lineHeight:"100%"
                }}>
                {/* Start Date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography 
                    sx={{ 
                      fontSize: "14px", 
                      fontWeight: 600, 
                      color: "#333", 
                      whiteSpace: "nowrap"
                    }}
                  >
                    Start Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={startDate}
                      onChange={(newValue) => {
                        setStartDate(newValue);
                        setStartDateChanged(true);
                        // Validate start date - check if empty or in the past
                        if (newValue) {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (newValue < today) {
                            setStartDateError("Start date cannot be earlier than today");
                          } else {
                            setStartDateError("");
                          }
                        } else {
                          setStartDateError("Start date is mandatory");
                        }
                        // Check if end date is now invalid
                        if (endDate && newValue && endDate < newValue) {
                          setEndDateError("End date cannot be earlier than start date");
                        } else {
                          setEndDateError("");
                        }
                      }}
                      format="dd MMM yyyy"
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!startDateError,
                          sx: {
                            width: "180px",
                            "& .MuiOutlinedInput-root": {
                              height: "36px",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "#717273",
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* End Date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography 
                    sx={{ 
                      fontSize: "14px", 
                      fontWeight: 600, 
                      color: "#333", 
                      whiteSpace: "nowrap"
                    }}
                  >
                    End Date {forceUninstall ? (
                      <span style={{ color: "red" }}>*</span>
                    ) : (
                      <span style={{ color: "#717273", fontWeight:'400', fontSize:'14px',lineHeight:"100%", fontStyle:"italic" }}>(Optional)</span>
                    )}
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={endDate}
                      onChange={(newValue) => {
                        setEndDate(newValue);
                        // Validate end date - check if user is trying to remove existing end date
                        if (!newValue && endDateUTCString) {
                          setEndDateError("You cannot remove end date");
                        } else if (!newValue && forceUninstall) {
                          setEndDateError("End date is required when Force Uninstall Upon Expiry is enabled");
                        } else if (newValue && startDate && newValue < startDate) {
                          setEndDateError("End date cannot be earlier than start date");
                        } else {
                          setEndDateError("");
                        }
                      }}
                      format="dd MMM yyyy"
                      minDate={startDate}
                      disablePast
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!endDateError,
                          sx: {
                            width: "180px",
                            "& .MuiOutlinedInput-root": {
                              height: "36px",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "#717273",
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* Force Uninstall Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceUninstall}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setForceUninstall(isChecked);
                        // If force uninstall is checked and no end date, show error
                        if (isChecked && !endDate) {
                          setEndDateError("End date is required when Force Uninstall Upon Expiry is enabled");
                        } else if (!isChecked && endDateUTCString && !endDate) {
                          setEndDateError("You cannot remove end date");
                        } else {
                          setEndDateError("");
                        }
                      }}
                      disabled={sessionStorage.getItem("readonly_OemAdmin") === "true"}
                      size="small"
                      sx={{
                        color: "#666",
                        '&.Mui-checked': {
                          color: "#0066cc",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontSize: "14px", 
                       whiteSpace: "nowrap",
                       fontWeight:"500", 
                       lineHeight:"20px", 
                       letterSpacing:'0.25px', 
                       color:"#000000"
                       }}>
                      Force Uninstall Upon Expiry
                    </Typography>
                  }
                  sx={{ margin: 0, ml: 3 }}
                />

                {/* Modify Button */}
                <Box sx={{ ml: "auto" }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      borderColor: "#0476E2",
                      color: "#0476E2",
                      fontSize: "13.5px",
                      fontWeight: 500,
                      lineHeight:"100%",
                      px: 2,
                      py: 0.75,
                      height: "36px",
                      borderRadius: "4px",
                      padding: "10px 14px 10px 14px",
                      "&:hover": {
                        borderColor: "#0052a3",
                        backgroundColor: "rgba(4, 118, 226, 0.04)",
                      },
                    }}
                    onClick={handleSaveDates}
                    disabled={!startDate || !!startDateError || !!endDateError || (forceUninstall && !endDate) || sessionStorage.getItem("readonly_OemAdmin") === "true"}
                  >
                    Modify
                  </Button>
                </Box>
              </Box>

              {/* Error messages row */}
              {(startDateError || endDateError) && (
                <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                  {startDateError && (
                    <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>
                      {startDateError}
                    </Typography>
                  )}
                  {endDateError && (
                    <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>
                      {endDateError}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          <div style={{ margin: "8px 8px 8px 0" }}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                border: "1px solid #c6c6c6",
                borderRadius: "4px",
                maxHeight: "210px",
                overflowY: "auto"
              }}
            >
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <StyledTableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                      backgroundColor: "#f8f9fa",
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      verticalAlign: "middle",
                    }}
                  >
                    Vehicle ID
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                      backgroundColor: "#f8f9fa",
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      verticalAlign: "middle",
                    }}
                  >
                    Friendly Name
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                      backgroundColor: "#f8f9fa",
                      width: "80px",
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      verticalAlign: "middle",
                    }}
                  >
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={3} align="center" sx={{ py: 2 }}>
                      <Typography sx={{ color: "#717273", fontStyle: "italic" }}>
                        No vehicles found in this list
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <StyledTableRow key={vehicle.id || vehicle.vehicleId || index}>
                      <StyledTableCell
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "bold",
                        }}
                      >
                        {vehicle.id || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {vehicle.description || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <IconButton
                          onClick={() => handleDeleteVehicle(vehicle)}
                          disabled={sessionStorage.getItem("readonly_OemAdmin") === "true"}
                          size="small"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              color: "#d32f2f",
                              backgroundColor: "#ffebee",
                            },
                          }}
                        >
                          <CustomDeleteIcon />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </div>
          </div>
        )}

        {/* Associated Apps Section */}
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#333",
              mb: 1,
            }}
          >
            Associated Apps ({associatedPackages ? associatedPackages.length : 0})
          </Typography>
          
          {/* Apps List */}
          {associatedPackages && associatedPackages.length > 0 ? (
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                backgroundColor: "#fff",
                maxHeight: "150px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {associatedPackages.map((app, index) => (
                <Box
                  key={app.packageId || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: index < associatedPackages.length - 1 ? "1px solid #ddd" : "none",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {/* App Icon */}
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #ddd",
                      marginRight: 2,
                      flexShrink: 0,
                    }}
                  >
                    {app.icon ? (
                      <img
                        src={app.icon}
                        alt={app.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <Box
                      sx={{
                        display: app.icon ? "none" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        fontSize: "20px",
                      }}
                    >
                      📱
                    </Box>
                  </Box>
                  
                  {/* App Title */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {app.title || "Unknown App"}
                    </Typography>
                  </Box>
                  
                  {/* Package ID */}
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      fontFamily: "monospace",
                      marginLeft: 2,
                      flexShrink: 0,
                    }}
                  >
                    {app.packageId}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                padding: "24px",
                textAlign: "center",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                border: "1px dashed #d0d0d0",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#717273",
                  fontStyle: "italic",
                }}
              >
                No apps associated with this vehicle list
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    
    {/* Toast for error messages */}
    <Snackbar
      open={openSnackbar}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      message={snackbarMessage}
    />

    {/* Add Vehicle Dialog */}
    <AddVehicleDialog
      open={addVehicleDialogOpen}
      onClose={handleCloseAddVehicleDialog}
      onSubmit={handleAddVehicleSubmit}
      onError={handleAddVehicleError}
      portfolioId={portfolioId}
      vehicleListId={vehicleListId}
    />

    {/* Delete Confirmation Dialog */}
    <DeleteVehicleConfirmationDialog
      open={deleteDialogOpen}
      onClose={handleCancelDelete}
      onConfirm={handleConfirmDelete}
      vehicleToDelete={vehicleToDelete}
    />
    </>
  );
}

VehicleDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleListName: PropTypes.string,
  portfolioId: PropTypes.string,
  vehicleListId: PropTypes.string,
  associatedPackages: PropTypes.arrayOf(
    PropTypes.shape({
      packageId: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.string,
    })
  ),
  startDateUTCString: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endDateUTCString: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  forceUninstallRequired: PropTypes.bool,
  onRefresh: PropTypes.func,
};
